<template>
  <!-- Prevent mousedown from propagating to editor - critical for clean resize handle drags -->
  <div
    ref="overlayEl"
    v-if="visible && tableMetadata"
    class="superdoc-table-resize-overlay"
    :style="overlayStyle"
    @mousedown.stop
  >
    <!-- Resize handles for each column boundary segment -->
    <template
      v-for="(boundary, resizableBoundaryIndex) in resizableBoundaries"
      :key="`boundary-${resizableBoundaryIndex}`"
    >
      <div
        v-for="(segment, segmentIndex) in getBoundarySegments(boundary)"
        v-show="!rowDragState"
        :key="`handle-${boundary.type}-${boundary.index}-${segmentIndex}`"
        class="resize-handle"
        :class="{
          'resize-handle--active': dragState && dragState.resizableBoundaryIndex === resizableBoundaryIndex,
          'resize-handle--edge': boundary.type === 'right-edge',
        }"
        :data-boundary-index="resizableBoundaryIndex"
        :data-boundary-type="boundary.type"
        :style="getSegmentHandleStyle(boundary, segment)"
        @mousedown="onHandleMouseDown($event, resizableBoundaryIndex)"
      ></div>
    </template>

    <!-- Visual guideline during column drag -->
    <div v-if="dragState" class="resize-guideline" :style="guidelineStyle"></div>

    <!-- Resize handles for each row boundary -->
    <div
      v-for="(rowBoundary, rowBoundaryIndex) in resizableRowBoundaries"
      v-show="!dragState"
      :key="`row-handle-${rowBoundary.i}`"
      class="resize-handle resize-handle--row"
      :class="{
        'resize-handle--active': rowDragState && rowDragState.rowBoundaryIndex === rowBoundaryIndex,
      }"
      :data-row-boundary-index="rowBoundaryIndex"
      :style="getRowHandleStyle(rowBoundary)"
      @mousedown="onRowHandleMouseDown($event, rowBoundaryIndex)"
    ></div>

    <!-- Visual guideline during row drag -->
    <div v-if="rowDragState" class="resize-guideline resize-guideline--row" :style="rowGuidelineStyle"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { pixelsToTwips, twipsToPixels } from '@core/super-converter/helpers.js';
import { measureCache, invalidateHeaderFooterMeasureCache } from '@superdoc/layout-bridge';
import { buildWidthAuthoringTableAttrs } from '../document-api-adapters/helpers/table-attr-sync.js';

/**
 * Props for the TableResizeOverlay component
 */
const props = defineProps({
  /** Editor instance for dispatching transactions */
  editor: {
    type: Object,
    required: true,
  },
  /** Show or hide the overlay */
  visible: {
    type: Boolean,
    default: false,
  },
  /** Table fragment element containing data-table-boundaries */
  tableElement: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['resize-start', 'resize-move', 'resize-end', 'resize-success', 'resize-error']);

const overlayEl = ref(null);
const overlayRect = ref(null);
const tableScreenWidth = computed(() => `${overlayRect.value?.width ?? 0}px`);
const tableScreenHeight = computed(() => `${overlayRect.value?.height ?? 0}px`);
const isViewingMode = () => props.editor?.options?.documentMode === 'viewing';
/**
 * Parsed table metadata from data-table-boundaries attribute
 * @type {import('vue').Ref<{columns: Array<{i: number, x: number, w: number, min: number, r?: number}>} | null>}
 */
const tableMetadata = ref(null);

/**
 * Normalize metadata-provided minimum width so resize remains possible.
 * Some imported tables report min == current width (e.g. 100/100), which
 * clamps drag delta to zero and makes columns feel "stuck".
 *
 * @param {number} width - Current column width in layout pixels
 * @param {number} rawMin - Raw minimum width from metadata
 * @returns {number}
 */
function normalizeColumnMinWidth(width, rawMin) {
  const safeWidth = Math.max(1, Number(width) || 1);
  const safeMin = Math.max(1, Number(rawMin) || 1);
  if (safeMin < safeWidth) return safeMin;

  // Keep at least a practical shrink budget while guaranteeing min < width.
  if (safeWidth <= 2) return 1;

  const candidate = Math.max(1, Math.max(25, Math.floor(safeWidth * 0.5)));
  return Math.min(safeWidth - 1, candidate);
}

/**
 * Get the editor's zoom level for coordinate transformations.
 *
 * Retrieves the current zoom multiplier from the editor instance. Zoom is centrally
 * controlled by PresentationEditor via transform: scale() applied to the #viewportHost
 * element. This ensures consistent scaling between rendered content and overlay elements.
 *
 * The zoom factor is critical for accurate coordinate transformations:
 * - Layout coordinates: Unscaled logical pixels used by the layout engine
 * - Screen coordinates: Physical pixels affected by CSS transform: scale()
 * - Conversion: screenCoord = layoutCoord * zoom
 *
 * This function handles both direct PresentationEditor instances and wrapped Editor
 * instances that contain a presentationEditor property.
 *
 * The zoom level is a multiplier where:
 * - 1 = 100% (default, no scaling)
 * - 0.5 = 50% (zoomed out, screen coords are half of layout coords)
 * - 2 = 200% (zoomed in, screen coords are double layout coords)
 *
 * @returns {number} The zoom level multiplier. Returns 1 (100%) as a safe fallback
 *                   if zoom cannot be retrieved from the editor instance.
 *
 * @example
 * ```javascript
 * const zoom = getZoom();
 * // Position resize handle at column boundary
 * const boundaryScreenX = columnLayoutX * zoom;
 * // Check if mouse is within threshold of boundary
 * const mouseLayoutX = event.clientX / zoom;
 * ```
 */
const getZoom = () => {
  const editor = props.editor;
  if (editor && typeof editor.zoom === 'number') {
    return editor.zoom;
  }
  if (editor?.presentationEditor && typeof editor.presentationEditor.zoom === 'number') {
    return editor.presentationEditor.zoom;
  }
  // Fallback to default zoom when editor instance doesn't have zoom configured
  console.warn(
    '[TableResizeOverlay] getZoom: Unable to retrieve zoom from editor instance, using fallback value of 1. ' +
      'This may indicate the editor is not fully initialized or is not a PresentationEditor instance. ' +
      'Table resize handles may be misaligned.',
  );
  return 1;
};

/**
 * Drag state tracking
 * @type {import('vue').Ref<{
 *   columnIndex: number,
 *   resizableBoundaryIndex: number,
 *   isRightEdge: boolean,
 *   initialX: number,
 *   initialWidths: number[],
 *   leftColumn: {width: number, minWidth: number},
 *   rightColumn: {width: number, minWidth: number} | null,
 *   constrainedDelta: number
 * } | null>}
 */
const dragState = ref(null);

/**
 * Row drag state tracking (mutually exclusive with column drag)
 * @type {import('vue').Ref<{
 *   rowIndex: number,
 *   rowBoundaryIndex: number,
 *   initialY: number,
 *   initialHeight: number,
 *   minHeight: number,
 *   constrainedDelta: number
 * } | null>}
 */
const rowDragState = ref(null);

/**
 * Flag to track forced cleanup (overlay hidden during drag)
 */
const forcedCleanup = ref(false);

// ============================================================================
// Constants
// ============================================================================

/**
 * Width of the resize handle hit area in pixels.
 *
 * COORDINATE SPACE: This width is in SCREEN SPACE (zoomed pixels).
 * - The handle is positioned using layout coordinates (column boundary x * zoom)
 * - The width itself is a fixed screen-space value (9px) to ensure consistent hit area
 * - This means the handle maintains a constant visual size regardless of zoom level
 * - Users can always grab handles with the same precision, even when zoomed in/out
 *
 * Example: At zoom 2.0, a boundary at layout x=100 renders at screen x=200,
 * but the handle width stays 9 screen pixels for easy interaction.
 */
const RESIZE_HANDLE_WIDTH_PX = 9;

/** Height of the row resize handle hit area in pixels (screen space) */
const RESIZE_HANDLE_HEIGHT_PX = 9;

/**
 * Horizontal offset to center the resize handle on the boundary line.
 *
 * COORDINATE SPACE: This offset is in SCREEN SPACE (zoomed pixels).
 * - Applied via CSS transform: translateX() to center the 9px-wide handle
 * - Offset of 4px centers a 9px element on the boundary line (9/2 ≈ 4.5, rounded to 4)
 * - Like RESIZE_HANDLE_WIDTH_PX, this is zoom-independent for consistent UX
 *
 * Visual layout:
 *   Boundary line position: x
 *   Handle left edge: x - 4px
 *   Handle right edge: x + 5px (9px total width)
 *   Result: Boundary line is approximately centered in the handle
 */
const RESIZE_HANDLE_OFFSET_PX = 4;

/** Throttle interval for mouse move events (60fps = ~16.67ms) */
const THROTTLE_INTERVAL_MS = 16;

/** Minimum delta threshold to dispatch a resize transaction (avoids micro-adjustments) */
const MIN_RESIZE_DELTA_PX = 1;

// ============================================================================
// State
// ============================================================================

let rafId = null;
let isUnmounted = false;

function removeInteractionCancelListeners() {
  window.removeEventListener('blur', onInteractionCancel);
  document.removeEventListener('visibilitychange', onInteractionCancel);
}

function cancelActiveResizeDrag() {
  if (!dragState.value && !rowDragState.value) return;

  forcedCleanup.value = true;
  if (dragState.value) {
    onDocumentMouseUp(new MouseEvent('mouseup'));
  }
  if (rowDragState.value) {
    onRowDocumentMouseUp();
  }
  forcedCleanup.value = false;
}

function onInteractionCancel(event) {
  if (!dragState.value && !rowDragState.value) return;
  if (event?.type === 'visibilitychange' && document.visibilityState === 'visible') {
    return;
  }
  if (document.visibilityState && document.visibilityState !== 'visible') {
    cancelActiveResizeDrag();
    return;
  }
  cancelActiveResizeDrag();
}

/**
 * Starts continuous RAF-based tracking of the overlay position.
 *
 * Uses requestAnimationFrame to continuously update the overlay's position
 * relative to the table element. This ensures handles stay aligned during
 * scrolling, layout changes, and pagination shifts.
 *
 * @returns {void}
 * @sideeffect Sets `rafId` to the current animation frame ID
 * @sideeffect Calls `updateOverlayRect()` on each animation frame
 */
function startOverlayTracking() {
  if (rafId !== null) return;
  const step = () => {
    updateOverlayRect();
    rafId = requestAnimationFrame(step);
  };
  rafId = requestAnimationFrame(step);
}

/**
 * Stops the RAF-based overlay position tracking.
 *
 * Cancels any pending animation frame and resets the tracking state.
 * Should be called when the overlay is hidden or component unmounts.
 *
 * @returns {void}
 * @sideeffect Cancels the animation frame identified by `rafId`
 * @sideeffect Sets `rafId` to null
 */
function stopOverlayTracking() {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
}

/**
 * Overlay position and size relative to table element
 */
const overlayStyle = computed(() => {
  if (!overlayRect.value || !props.tableElement) return {};

  const rect = overlayRect.value;
  const isDragging = dragState.value || rowDragState.value;

  return {
    position: 'absolute',
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    pointerEvents: isDragging ? 'auto' : 'none',
    zIndex: 10,
  };
});

/**
 * Recompute overlay position/size relative to the overlay's own offset parent.
 * Keeps handles aligned when the table moves (scroll, relayout, pagination shifts).
 */
function updateOverlayRect() {
  if (!props.tableElement) {
    overlayRect.value = null;
    return;
  }

  const tableRect = props.tableElement.getBoundingClientRect();

  // Validate rect has non-zero dimensions
  if (tableRect.width === 0 || tableRect.height === 0) {
    overlayRect.value = null;
    return;
  }

  // Position overlay relative to its own offset parent (the element that
  // position:absolute resolves against), NOT the table's offset parent.
  // The table's offsetParent is its .superdoc-page, but the overlay is rendered
  // as a child of .super-editor — a different coordinate space. Using the table's
  // page as reference caused the overlay to be mispositioned on pages after the first.
  const referenceEl = overlayEl.value?.offsetParent;
  if (referenceEl) {
    const referenceRect = referenceEl.getBoundingClientRect();
    const left = tableRect.left - referenceRect.left + (referenceEl.scrollLeft || 0);
    const top = tableRect.top - referenceRect.top + (referenceEl.scrollTop || 0);
    overlayRect.value = {
      left,
      top,
      width: tableRect.width,
      height: tableRect.height,
    };
  } else {
    // Fallback: use table's own offset position
    overlayRect.value = {
      left: props.tableElement.offsetLeft,
      top: props.tableElement.offsetTop,
      width: tableRect.width,
      height: tableRect.height,
    };
  }
}

/**
 * Filter to only resizable column boundaries
 * Creates handles for:
 * - Inner boundaries (between columns)
 * - Right edge (resize last column)
 */
const isRtlTable = computed(() => tableMetadata.value?.rtl === true);

const tableContentWidth = computed(() => {
  const columns = tableMetadata.value?.columns;
  if (!columns || columns.length === 0) return 0;
  const last = columns[columns.length - 1];
  return last.x + last.w;
});

const tableContentLeft = computed(() => {
  const columns = tableMetadata.value?.columns;
  if (!columns || columns.length === 0) return 0;
  return columns[0].x;
});

const resizableBoundaries = computed(() => {
  if (!tableMetadata.value?.columns) {
    return [];
  }

  const columns = tableMetadata.value.columns;
  const boundaries = [];

  // Create handles for inner column boundaries (between columns)
  for (let i = 0; i < columns.length - 1; i++) {
    const col = columns[i];
    const nextCol = columns[i + 1];

    const logicalX = nextCol.x;
    const visualX = isRtlTable.value ? tableContentLeft.value + tableContentWidth.value - logicalX : logicalX;

    boundaries.push({
      ...col,
      index: i,
      x: visualX,
      type: 'inner',
    });
  }

  // Add handle for right edge of table (resize last column).
  // SD-2810 RTL note: the right-edge handle ALWAYS sits on the visual right
  // side of the table. In an LTR table that means the trailing edge of the
  // last logical column (`columns.length - 1`); in a `bidiVisual` RTL table
  // the visually-rightmost column is the FIRST logical column (`0`) because
  // cells are stored logically and rendered right-to-left. Downstream
  // consumers that need "is this the table's outer edge?" should key off
  // `type === 'right-edge'`, NOT `columnIndex === columns.length - 1`,
  // because that index equality is LTR-only.
  const lastCol = columns[columns.length - 1];
  const rtlRightEdgeX = tableContentWidth.value;
  boundaries.push({
    ...lastCol,
    index: isRtlTable.value ? 0 : columns.length - 1,
    x: isRtlTable.value ? rtlRightEdgeX : lastCol.x + lastCol.w,
    type: 'right-edge',
  });

  return boundaries;
});

/**
 * Filter to only resizable row boundaries.
 * Adds a computed handleY (bottom edge of the row) for positioning.
 */
const resizableRowBoundaries = computed(() => {
  if (!tableMetadata.value?.rows || !Array.isArray(tableMetadata.value.rows)) {
    return [];
  }

  return tableMetadata.value.rows
    .filter((row) => row.r === 1)
    .map((row) => ({
      ...row,
      handleY: row.y + row.h, // bottom edge of the row
    }));
});

/**
 * Retrieves vertical segments for a column boundary where resize handles should appear.
 *
 * Segments define the vertical ranges where a column boundary is visible and resizable,
 * accounting for merged cells that span multiple rows. A boundary at column index N
 * exists only where cells end at that column position.
 *
 * **Segment Structure:**
 * Each segment has:
 * - `y`: Vertical position in pixels from table top
 * - `h`: Height in pixels, or `null` for full-height (100%)
 *
 * **Right Edge Handling:**
 * Right-edge boundaries always span full height since they represent the table's
 * outer edge.
 *
 * **Merged Cell Handling:**
 * When cells span multiple columns, some boundaries don't exist at certain rows.
 * For example, if row 0 has a cell spanning columns 0-2, there's no boundary at
 * column 1 for that row.
 *
 * @param {{index: number, type: string, ...rest: unknown}} boundary - Column boundary data with index and type properties
 * @returns {Array<{y: number, h: number | null}>} Array of vertical segments where handles should render, or empty array if boundary is fully covered by merged cells
 *
 * @example
 * ```typescript
 * // Right edge boundary - always full height
 * getBoundarySegments({ index: 2, type: 'right-edge' })
 * // Returns: [{ y: 0, h: null }]
 *
 * // Inner boundary with segments at specific rows
 * getBoundarySegments({ index: 1, type: 'inner' })
 * // Returns: [{ y: 0, h: 50 }, { y: 100, h: 25 }]
 *
 * // Boundary completely covered by merged cells
 * getBoundarySegments({ index: 1, type: 'inner' })
 * // Returns: []
 * ```
 */
function getBoundarySegments(boundary) {
  // For right-edge, always show full height
  if (boundary.type === 'right-edge') {
    return [{ y: 0, h: null }]; // null height means 100%
  }

  // Get segments for this boundary column from metadata
  // The boundary at index N is between columns N and N+1
  // So we look up segments for column index N+1 (the right edge of column N)
  const segmentsData = tableMetadata.value?.segments;
  if (!segmentsData || !Array.isArray(segmentsData)) {
    // Fallback to full-height if no segments data
    return [{ y: 0, h: null }];
  }

  // boundary.index is the column index, the boundary is at boundary.index + 1
  const boundaryColIndex = boundary.index + 1;
  const colSegments = segmentsData[boundaryColIndex];

  if (!colSegments || colSegments.length === 0) {
    // No segments for this boundary - it's completely inside merged cells
    // Return empty array to hide handle entirely for this boundary
    return [];
  }

  return colSegments
    .filter((seg) => seg && typeof seg === 'object')
    .map((seg) => ({
      y: typeof seg.y === 'number' ? seg.y : 0,
      h: seg.h !== null && typeof seg.h === 'number' ? seg.h : null,
    }));
}

/**
 * Generates CSS styles for positioning a resize handle segment.
 *
 * Creates an absolutely-positioned element at the specified column boundary,
 * with vertical positioning and height determined by the segment. The handle
 * is offset horizontally to center it on the boundary line.
 *
 * **Positioning Logic:**
 * - Horizontal: Positioned at `boundary.x` with -4px transform to center the 9px-wide handle
 * - Vertical: Uses `segment.y` for top position, or 0 if null
 * - Height: Uses `segment.h` for pixel height, or '100%' if null
 *
 * **Interaction:**
 * - Cursor is set to 'col-resize' for visual feedback
 * - Pointer events enabled to capture mouse interactions
 *
 * @param {{x: number, index: number, ...rest: unknown}} boundary - Column boundary data containing x position
 * @param {{y: number | null, h: number | null}} segment - Segment position (y) and height (h), null values use defaults
 * @returns {Record<string, string>} CSS style object for the handle element
 *
 * @example
 * ```typescript
 * // Full-height handle at x=100
 * getSegmentHandleStyle({ x: 100, index: 0 }, { y: null, h: null })
 * // Returns: { position: 'absolute', left: '100px', top: '0', width: '9px', height: '100%', ... }
 *
 * // Segment handle from y=50 with height 75px
 * getSegmentHandleStyle({ x: 200, index: 1 }, { y: 50, h: 75 })
 * // Returns: { position: 'absolute', left: '200px', top: '50px', width: '9px', height: '75px', ... }
 * ```
 */
function getSegmentHandleStyle(boundary, segment) {
  // Get zoom factor to convert layout coordinates to screen coordinates
  const zoom = getZoom();

  // Multiply layout coordinates by zoom to position correctly in zoomed space
  const scaledX = boundary.x * zoom;
  const scaledY = segment.y != null ? segment.y * zoom : null;
  const scaledH = segment.h != null ? segment.h * zoom : null;

  return {
    position: 'absolute',
    left: `${scaledX}px`,
    top: scaledY != null ? `${scaledY}px` : '0',
    width: `${RESIZE_HANDLE_WIDTH_PX}px`,
    height: scaledH != null ? `${scaledH}px` : tableScreenHeight.value,
    transform: `translateX(-${RESIZE_HANDLE_OFFSET_PX}px)`,
    cursor: 'col-resize',
    pointerEvents: 'auto',
  };
}

/**
 * Style for the drag guideline
 */
const guidelineStyle = computed(() => {
  if (!dragState.value || !tableMetadata.value) return { display: 'none' };

  const initialBoundary = resizableBoundaries.value[dragState.value.resizableBoundaryIndex];
  if (!initialBoundary) return { display: 'none' };

  // Get zoom factor to convert layout coordinates to screen coordinates
  const zoom = getZoom();

  // constrainedDelta is in layout coordinates, so the entire calculation stays in layout space
  // Then multiply by zoom to convert to screen coordinates
  const newX = (initialBoundary.x + dragState.value.constrainedDelta) * zoom;

  return {
    position: 'absolute',
    left: `${newX}px`,
    top: '0',
    width: '2px',
    height: tableScreenHeight.value,
    backgroundColor: '#4A90E2',
    pointerEvents: 'none',
    zIndex: 20,
  };
});

/**
 * Generates CSS styles for positioning a row resize handle at the bottom edge of a row.
 * @param {{handleY: number}} rowBoundary - Row boundary with handleY in layout coords
 * @returns {Record<string, string>} CSS style object
 */
function getRowHandleStyle(rowBoundary) {
  const zoom = getZoom();
  const scaledY = rowBoundary.handleY * zoom;

  return {
    position: 'absolute',
    left: '0',
    top: `${scaledY}px`,
    width: tableScreenWidth.value,
    height: `${RESIZE_HANDLE_HEIGHT_PX}px`,
    transform: `translateY(-${RESIZE_HANDLE_OFFSET_PX}px)`,
    cursor: 'row-resize',
    pointerEvents: 'auto',
  };
}

/**
 * Style for the row drag guideline (horizontal line)
 */
const rowGuidelineStyle = computed(() => {
  if (!rowDragState.value || !tableMetadata.value) return { display: 'none' };

  const rowBoundary = resizableRowBoundaries.value[rowDragState.value.rowBoundaryIndex];
  if (!rowBoundary) return { display: 'none' };

  const zoom = getZoom();
  const newY = (rowBoundary.handleY + rowDragState.value.constrainedDelta) * zoom;

  return {
    position: 'absolute',
    left: '0',
    top: `${newY}px`,
    width: tableScreenWidth.value,
    height: '2px',
    backgroundColor: '#4A90E2',
    pointerEvents: 'none',
    zIndex: 20,
  };
});

/**
 * Parses table metadata from the `data-table-boundaries` attribute on the table element.
 *
 * Extracts column boundary information including positions, widths, and minimum widths.
 * Also extracts segment data for merged cell support when present.
 *
 * The metadata is validated to ensure:
 * - Column indices (`i`) are non-negative integers
 * - Column positions (`x`) are non-negative
 * - Column widths (`w`) are positive
 * - Minimum widths (`min`) are positive
 * - Resize flag (`r`) is 0 or 1
 *
 * @returns {void}
 * @sideeffect Sets `tableMetadata.value` to parsed metadata or null
 * @emits resize-error When metadata is corrupted, empty after validation, or fails to parse
 *
 * @example
 * ```typescript
 * // Called automatically when tableElement prop changes
 * // Metadata format: { columns: [...], segments: [...] }
 * parseTableMetadata();
 * // tableMetadata.value = { columns: [{i: 0, x: 0, w: 100, min: 50, r: 1}, ...], segments: [...] }
 * ```
 */
function parseTableMetadata() {
  if (!props.tableElement) {
    tableMetadata.value = null;
    return;
  }

  try {
    const boundariesAttr = props.tableElement.getAttribute('data-table-boundaries');
    if (!boundariesAttr) {
      tableMetadata.value = null;
      return;
    }

    const parsed = JSON.parse(boundariesAttr);
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.columns)) {
      tableMetadata.value = null;
      return;
    }

    const validatedColumns = parsed.columns
      .filter((col) => {
        return (
          typeof col === 'object' &&
          Number.isFinite(col.i) &&
          col.i >= 0 &&
          Number.isFinite(col.x) &&
          col.x >= 0 &&
          Number.isFinite(col.w) &&
          col.w > 0 &&
          Number.isFinite(col.min) &&
          col.min > 0 &&
          (col.r === 0 || col.r === 1)
        );
      })
      .map((col) => ({
        w: Math.max(1, col.w),
        min: normalizeColumnMinWidth(col.w, col.min),
        i: col.i,
        x: Math.max(0, col.x),
        r: col.r,
      }));

    // Check for corrupted metadata - valid JSON but empty/invalid structure
    if (validatedColumns.length === 0) {
      tableMetadata.value = null;
      emit('resize-error', {
        error: 'Table metadata is corrupted or empty after validation',
        rawMetadata: boundariesAttr,
      });
      return;
    }

    // Extract segments if present (for merged cell support)
    // segments[colIndex] contains segment data for that column boundary
    // Each segment has {c: columnIndex, y: yPosition, h: height}
    const segments = Array.isArray(parsed.segments) ? parsed.segments : undefined;

    // Extract row boundaries if present (for row resizing)
    const rows = Array.isArray(parsed.rows)
      ? parsed.rows.filter(
          (row) =>
            typeof row === 'object' &&
            Number.isFinite(row.i) &&
            row.i >= 0 &&
            Number.isFinite(row.y) &&
            row.y >= 0 &&
            Number.isFinite(row.h) &&
            row.h > 0 &&
            Number.isFinite(row.min) &&
            row.min > 0 &&
            (row.r === 0 || row.r === 1),
        )
      : undefined;

    tableMetadata.value = { columns: validatedColumns, segments, rows, rtl: parsed.rtl === true };
  } catch (error) {
    tableMetadata.value = null;
    emit('resize-error', {
      error: error instanceof Error ? error.message : 'Failed to parse table boundaries',
      rawMetadata: props.tableElement?.getAttribute('data-table-boundaries'),
    });
  }
}

/**
 * Handle mouse down on resize handle
 * @param {MouseEvent} event - Mouse event
 * @param {number} resizableBoundaryIndex - Index in the resizableBoundaries array
 */
function onHandleMouseDown(event, resizableBoundaryIndex) {
  event.preventDefault();
  event.stopPropagation();

  if (isViewingMode()) {
    return;
  }

  if (!tableMetadata.value?.columns) return;

  const boundary = resizableBoundaries.value[resizableBoundaryIndex];
  if (!boundary) return;

  const columns = tableMetadata.value.columns;
  const isRightEdge = boundary.type === 'right-edge';

  const leftColumn = columns[boundary.index];
  const rightColumn = isRightEdge ? null : columns[boundary.index + 1];

  // Store initial state
  dragState.value = {
    columnIndex: boundary.index,
    resizableBoundaryIndex,
    isRightEdge,
    initialX: event.clientX,
    initialWidths: columns.map((col) => col.w),
    leftColumn: {
      width: leftColumn.w,
      minWidth: leftColumn.min,
    },
    rightColumn: rightColumn
      ? {
          width: rightColumn.w,
          minWidth: rightColumn.min,
        }
      : null,
    constrainedDelta: 0,
  };

  // Disable pointer events on PM view to prevent conflicts
  if (!props.editor?.view?.dom) {
    emit('resize-error', { error: 'Editor view not available' });
    dragState.value = null;
    return;
  }
  const pmView = props.editor.view.dom;
  pmView.style.pointerEvents = 'none';

  // Add global listeners
  try {
    document.addEventListener('mousemove', onDocumentMouseMove);
    document.addEventListener('mouseup', onDocumentMouseUp);
    window.addEventListener('blur', onInteractionCancel);
    document.addEventListener('visibilitychange', onInteractionCancel);

    emit('resize-start', {
      columnIndex: boundary.index,
      isRightEdge,
      initialWidths: dragState.value.initialWidths,
    });
  } catch (error) {
    document.removeEventListener('mousemove', onDocumentMouseMove);
    document.removeEventListener('mouseup', onDocumentMouseUp);
    pmView.style.pointerEvents = 'auto';
    dragState.value = null;
    emit('resize-error', { error: error instanceof Error ? error.message : String(error) });
  }
}

/**
 * Throttle function with cancellation support to prevent memory leaks
 * @param {Function} func - Function to throttle
 * @param {number} limit - Minimum time between executions (ms)
 * @returns {{throttled: Function, cancel: Function}} Throttled function and cancel function
 */
function throttle(func, limit) {
  let inThrottle;
  let timeoutId = null;

  const throttled = function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      timeoutId = setTimeout(() => {
        inThrottle = false;
        timeoutId = null;
      }, limit);
    }
  };

  const cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
      inThrottle = false;
    }
  };

  return { throttled, cancel };
}

// Create throttled mouse move handler with cancellation
const mouseMoveThrottle = throttle((event) => {
  if (isUnmounted || !dragState.value) return;

  // Get zoom factor to convert screen coordinates to layout coordinates
  const zoom = getZoom();

  // Calculate raw delta in screen pixels, then convert to layout space
  // This ensures constraints (which are in layout space) can be compared correctly
  const screenDelta = event.clientX - dragState.value.initialX;
  const visualDelta = screenDelta / zoom;
  const delta = isRtlTable.value && !dragState.value.isRightEdge ? -visualDelta : visualDelta;

  // Calculate constraints based on layout-computed minWidth (already in layout space)
  const minDelta = -(dragState.value.leftColumn.width - dragState.value.leftColumn.minWidth);

  // For right edge, constrain by page content area to prevent overflow beyond margins
  // For inner boundaries, constrain by right column's minimum
  let maxDelta;
  if (dragState.value.isRightEdge) {
    // Get the page element (superdoc-page) which represents the physical page
    const tableRect = props.tableElement.getBoundingClientRect();
    const pageEl = props.tableElement.closest('.superdoc-page');

    if (pageEl) {
      const pageRect = pageEl.getBoundingClientRect();
      const tableLeftInPage = tableRect.left - pageRect.left;
      const rightMargin = tableLeftInPage; // Assumes symmetric margins
      const maxRightPosition = pageRect.right - rightMargin;
      // availableSpace is in screen pixels (from getBoundingClientRect),
      // convert to layout space by dividing by zoom
      const availableSpace = (maxRightPosition - tableRect.right) / zoom;
      maxDelta = Math.max(0, availableSpace);
    } else {
      // No page element found - allow unlimited expansion (fallback)
      maxDelta = Infinity;
    }
  } else {
    // rightColumn dimensions are already in layout space
    maxDelta = dragState.value.rightColumn.width - dragState.value.rightColumn.minWidth;
  }

  // Constrain delta
  const constrainedDelta = Math.max(minDelta, Math.min(maxDelta, delta));
  const constrainedVisualDelta =
    isRtlTable.value && !dragState.value.isRightEdge ? -constrainedDelta : constrainedDelta;

  // Update visual guideline only (no PM transaction yet).
  // `constrainedDelta` on dragState stays in VISUAL coordinates so the preview
  // guideline at line 591 (`initialBoundary.x + dragState.value.constrainedDelta`)
  // tracks the cursor. The emitted `delta` below is LOGICAL: it matches the
  // value applied to `newWidths` and preserves the pre-PR contract for
  // external listeners (logging, analytics, undo metadata).
  dragState.value.constrainedDelta = constrainedVisualDelta;

  emit('resize-move', {
    columnIndex: dragState.value.columnIndex,
    delta: constrainedDelta,
  });
}, THROTTLE_INTERVAL_MS);

/** Handle mouse move during drag (throttled to 16ms for 60fps) */
const onDocumentMouseMove = mouseMoveThrottle.throttled;

/**
 * Handle mouse up to end drag
 * @param {MouseEvent} event - Mouse event
 */
function onDocumentMouseUp(event) {
  if (!dragState.value) return;

  const visualFinalDelta = dragState.value.constrainedDelta;
  const finalDelta = isRtlTable.value && !dragState.value.isRightEdge ? -visualFinalDelta : visualFinalDelta;
  const columnIndex = dragState.value.columnIndex;
  const initialWidths = dragState.value.initialWidths;
  const isRightEdge = dragState.value.isRightEdge;

  // Calculate final column widths
  const newWidths = [...initialWidths];
  newWidths[columnIndex] = initialWidths[columnIndex] + finalDelta;

  // Only adjust right column if this is an inner boundary (not right edge)
  if (!isRightEdge) {
    newWidths[columnIndex + 1] = initialWidths[columnIndex + 1] - finalDelta;
  }

  // Clean up event listeners and restore pointer events
  document.removeEventListener('mousemove', onDocumentMouseMove);
  document.removeEventListener('mouseup', onDocumentMouseUp);
  removeInteractionCancelListeners();

  if (props.editor?.view?.dom) {
    const pmView = props.editor.view.dom;
    pmView.style.pointerEvents = 'auto';
  }

  // Only dispatch transaction if not a forced cleanup and delta is significant
  if (!forcedCleanup.value && Math.abs(finalDelta) > MIN_RESIZE_DELTA_PX) {
    dispatchResizeTransaction(columnIndex, newWidths, isRightEdge);
  }

  // Always emit resize-end so the parent can clear its dragging flag.
  // `delta` is the LOGICAL change applied to `newWidths`, matching the
  // pre-PR contract. In LTR this equals `visualFinalDelta`; in RTL inner
  // boundaries it is the sign-flipped value.
  emit('resize-end', {
    columnIndex,
    finalWidths: newWidths,
    delta: finalDelta,
  });

  dragState.value = null;
}

/**
 * Dispatch ProseMirror transaction to update column widths
 * Updates both grid (twips) and colwidth (pixels) attributes
 *
 * @param {number} columnIndex - Index of the resized column
 * @param {number[]} newWidths - New column widths in pixels
 * @param {boolean} isRightEdge - True if the resize originated from the
 *   table's outer right-edge handle. Only the originating column's cells
 *   should be rewritten; passing `[columnIndex, columnIndex + 1]` for
 *   right-edge drags would unconditionally overwrite the next column's
 *   per-cell `cellWidth` (OOXML w:tcW) with the grid value, destroying
 *   any authored divergent tcW on merged or width-overridden cells.
 *   In LTR `columnIndex + 1` is past the end and updateCellColwidths
 *   no-ops, masking the issue; in RTL the right-edge handle maps to
 *   column 0 (see resizableBoundaries) so column 1 cells are real
 *   targets and the destructive write would occur.
 */
/**
 * Header/footer tables are owned by sub-editors. Their painted `data-pm-start`
 * values collide with body positions, so resolving against the body doc resizes
 * the wrong table (SD-4370). Returns null when the story is header/footer but
 * no matching sub-editor can be resolved — callers must refuse the commit rather
 * than fall back to the body editor.
 */
function resolveEditorForTableElement(editor, tableElement) {
  if (!editor || !tableElement) return editor;
  const headerFooterStory = getHeaderFooterStory(tableElement);
  if (!headerFooterStory) return editor;
  const { kind, id } = headerFooterStory;
  if (!id) return null;
  const converter = editor.converter;
  const collection = kind === 'header' ? converter?.headerEditors : converter?.footerEditors;
  const variantIds = kind === 'header' ? converter?.headerIds : converter?.footerIds;
  const concreteId = typeof variantIds?.[id] === 'string' ? variantIds[id] : id;
  if (Array.isArray(collection)) {
    const match = collection.find((item) => item?.id === id || item?.id === concreteId);
    if (match?.editor) return match.editor;
  }
  // Painted H/F tables are targetable before a session registers the sub-editor;
  // ensure a persistent registry editor so the commit owns the furniture story.
  return editor.presentationEditor?.ensureHeaderFooterEditor?.(kind, concreteId) ?? null;
}

function readElementOrClosestAttribute(tableElement, attributeName) {
  const direct = typeof tableElement.getAttribute === 'function' ? tableElement.getAttribute(attributeName) : null;
  if (typeof direct === 'string' && direct.length > 0) return direct;
  if (typeof tableElement.closest !== 'function') return '';
  return tableElement.closest(`[${attributeName}]`)?.getAttribute?.(attributeName) || '';
}

function getHeaderFooterStory(tableElement) {
  const layoutStory = readElementOrClosestAttribute(tableElement, 'data-layout-story');
  if (!layoutStory || layoutStory === 'body') return null;
  const separator = layoutStory.indexOf(':');
  const kind = separator === -1 ? layoutStory : layoutStory.slice(0, separator);
  const storyId = separator === -1 ? '' : layoutStory.slice(separator + 1);
  if (kind !== 'header' && kind !== 'footer') return null;
  // Painter may stamp a concrete rId on the decoration container even when the
  // fragment story is variant-only (`footer:default`) or kind-only (`footer`).
  const id =
    readElementOrClosestAttribute(tableElement, 'data-sd-headerfooter-ref-id') ||
    storyId ||
    readElementOrClosestAttribute(tableElement, 'data-sd-headerfooter-variant');
  return { kind, id };
}

function invalidateTableMeasureCache(tableElement) {
  const blockId = tableElement?.getAttribute('data-sd-block-id');
  if (!blockId || !blockId.trim()) return;
  if (getHeaderFooterStory(tableElement)) {
    invalidateHeaderFooterMeasureCache([blockId]);
    return;
  }
  measureCache.invalidate([blockId]);
}

function dispatchResizeTransaction(columnIndex, newWidths, isRightEdge = false) {
  if (!props.tableElement) {
    return;
  }
  const targetEditor = resolveEditorForTableElement(props.editor, props.tableElement);
  if (targetEditor === null) {
    emit('resize-error', {
      columnIndex,
      error: 'Header/footer editor not available for table resize',
    });
    return;
  }
  const view = targetEditor?.view;
  if (!view) {
    emit('resize-error', {
      columnIndex,
      error: 'Editor view not available for table resize',
    });
    return;
  }

  try {
    const { state, dispatch } = view;
    const tr = state.tr;

    // Find table position
    const tablePos = findTablePosition(state, props.tableElement);

    if (tablePos === null) {
      emit('resize-error', {
        columnIndex,
        error: 'Table position not found in document',
      });
      return;
    }

    // Get table node
    const tableNode = state.doc.nodeAt(tablePos);

    if (!tableNode || tableNode.type.name !== 'table') {
      emit('resize-error', {
        columnIndex,
        error: 'Invalid table node at position',
      });
      return;
    }

    // Convert pixel widths to twips for grid attribute
    const gridTwips = newWidths.map((w) => pixelsToTwips(w));
    const newGrid = gridTwips.map((twips) => ({ col: twips }));

    // Width drags are explicit authoring, so keep runtime attrs and DOCX
    // export state aligned on fixed layout immediately.
    const newAttrs = buildWidthAuthoringTableAttrs(tableNode.attrs, {
      grid: newGrid,
    });

    tr.setNodeMarkup(tablePos, null, newAttrs);

    // Update affected cell colwidth attributes. Right-edge drags only resize
    // the originating column (no adjacent column shrinks to compensate); see
    // mouseUp's `if (!isRightEdge) { newWidths[columnIndex + 1] -= finalDelta; }`
    // for the matching `newWidths` shape.
    const affectedColumns = isRightEdge ? [columnIndex] : [columnIndex, columnIndex + 1];
    updateCellColwidths(tr, tableNode, tablePos, affectedColumns, newWidths);

    // Dispatch transaction
    dispatch(tr);

    // The table block ID is story-local, so only invalidate its owning cache.
    invalidateTableMeasureCache(props.tableElement);

    // Emit success event
    emit('resize-success', { columnIndex, newWidths });
  } catch (error) {
    emit('resize-error', {
      columnIndex,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Find the position of the table node in the document
 * @param {Object} state - ProseMirror state
 * @param {HTMLElement} tableElement - Table DOM element
 * @returns {number | null} Position of table node or null
 */
function findTablePosition(state, tableElement) {
  // Strategy: Use ProseMirror position markers (data-pm-start/data-pm-end) from table cells
  // to find which table node in the document matches this DOM element

  // Find any element with data-pm-start inside the table
  const pmElement = tableElement.querySelector('[data-pm-start]');

  if (!pmElement) {
    return null;
  }

  const pmStartAttr = pmElement.getAttribute('data-pm-start');
  if (!pmStartAttr) {
    return null;
  }

  const pmStart = parseInt(pmStartAttr, 10);
  if (!Number.isFinite(pmStart)) {
    return null;
  }

  // Find the table node that contains this position
  let tablePos = null;
  state.doc.descendants((node, pos) => {
    if (node.type.name === 'table') {
      const tableEnd = pos + node.nodeSize;

      // Check if pmStart is within this table's range
      if (pmStart >= pos && pmStart < tableEnd) {
        tablePos = pos;
        return false; // Stop iteration
      }
    }
  });

  return tablePos;
}

/**
 * Update colwidth attributes on cells in affected columns
 * Uses ProseMirror's descendants API for proper position resolution
 * @param {Object} tr - ProseMirror transaction
 * @param {Object} tableNode - Table node
 * @param {number} tablePos - Position of table node
 * @param {number[]} affectedColumns - Column indices that changed
 * @param {number[]} newWidths - New column widths in pixels
 */
function updateCellColwidths(tr, tableNode, tablePos, affectedColumns, newWidths) {
  let currentRow = 0;
  let currentCol = 0;

  tableNode.descendants((node, pos, parent) => {
    if (node.type.name === 'tableRow') {
      currentCol = 0;
      return true; // Continue descending
    }

    if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
      const { colspan = 1 } = node.attrs;

      // Check if this cell is in an affected column
      const cellAffectsColumns = affectedColumns.some(
        (affectedCol) => affectedCol >= currentCol && affectedCol < currentCol + colspan,
      );

      if (cellAffectsColumns) {
        // Calculate absolute position in document
        const absolutePos = tablePos + 1 + pos;

        // Build new colwidth array for this cell
        const newColwidth = [];
        for (let i = 0; i < colspan; i++) {
          const colIndex = currentCol + i;
          const width = newWidths[colIndex];
          if (width !== undefined && width > 0) {
            newColwidth.push(width);
          }
        }

        // Only update if we have valid widths
        // colwidth must always be an array, even for single columns
        if (newColwidth.length > 0) {
          // Mirror the new span width into tableCellProperties.cellWidth (OOXML w:tcW).
          // The fixed-layout measuring solver treats tcW as authoritative for first-row
          // cells, so without this the next measure pass would override the edited
          // grid and snap the column back to its imported width.
          const spanTwips = newColwidth.reduce((sum, w) => sum + pixelsToTwips(w), 0);
          const existingTableCellProperties = node.attrs.tableCellProperties;
          const tableCellProperties = {
            ...(existingTableCellProperties && typeof existingTableCellProperties === 'object'
              ? existingTableCellProperties
              : {}),
            cellWidth: { value: spanTwips, type: 'dxa' },
          };

          tr.setNodeMarkup(absolutePos, null, {
            ...node.attrs,
            colwidth: newColwidth,
            tableCellProperties,
          });
        }
      }

      currentCol += colspan;
      return false; // Don't descend into cell content
    }

    return true;
  });
}

// ============================================================================
// Row Resize Drag Handlers
// ============================================================================

/**
 * Handle mouse down on a row resize handle
 * @param {MouseEvent} event - Mouse event
 * @param {number} rowBoundaryIndex - Index in the resizableRowBoundaries array
 */
function onRowHandleMouseDown(event, rowBoundaryIndex) {
  event.preventDefault();
  event.stopPropagation();

  if (isViewingMode()) {
    return;
  }

  const rowBoundary = resizableRowBoundaries.value[rowBoundaryIndex];
  if (!rowBoundary) return;

  rowDragState.value = {
    rowIndex: rowBoundary.i,
    rowBoundaryIndex,
    initialY: event.clientY,
    initialHeight: rowBoundary.h,
    minHeight: rowBoundary.min,
    constrainedDelta: 0,
  };

  if (!props.editor?.view?.dom) {
    emit('resize-error', { error: 'Editor view not available' });
    rowDragState.value = null;
    return;
  }
  props.editor.view.dom.style.pointerEvents = 'none';

  document.addEventListener('mousemove', onRowDocumentMouseMove);
  document.addEventListener('mouseup', onRowDocumentMouseUp);
  window.addEventListener('blur', onInteractionCancel);
  document.addEventListener('visibilitychange', onInteractionCancel);

  emit('resize-start', { rowIndex: rowBoundary.i });
}

// Throttled row mouse move handler
const rowMouseMoveThrottle = throttle((event) => {
  if (isUnmounted || !rowDragState.value) return;

  const zoom = getZoom();
  const screenDelta = event.clientY - rowDragState.value.initialY;
  const delta = screenDelta / zoom;

  // Constrain: can't shrink below minHeight, no upper limit
  const minDelta = -(rowDragState.value.initialHeight - rowDragState.value.minHeight);
  const constrainedDelta = Math.max(minDelta, delta);

  rowDragState.value.constrainedDelta = constrainedDelta;

  emit('resize-move', { rowIndex: rowDragState.value.rowIndex, delta: constrainedDelta });
}, THROTTLE_INTERVAL_MS);

const onRowDocumentMouseMove = rowMouseMoveThrottle.throttled;

/**
 * Handle mouse up to end row drag
 */
function onRowDocumentMouseUp() {
  if (!rowDragState.value) return;

  const finalDelta = rowDragState.value.constrainedDelta;
  const rowIndex = rowDragState.value.rowIndex;
  const newHeight = rowDragState.value.initialHeight + finalDelta;

  document.removeEventListener('mousemove', onRowDocumentMouseMove);
  document.removeEventListener('mouseup', onRowDocumentMouseUp);
  removeInteractionCancelListeners();

  if (props.editor?.view?.dom) {
    props.editor.view.dom.style.pointerEvents = 'auto';
  }

  if (!forcedCleanup.value && Math.abs(finalDelta) > MIN_RESIZE_DELTA_PX) {
    dispatchRowResizeTransaction(rowIndex, newHeight);
  }

  // Always emit resize-end so the parent can clear its dragging flag
  emit('resize-end', { rowIndex, newHeight, delta: finalDelta });

  rowDragState.value = null;
}

/**
 * Dispatch ProseMirror transaction to update row height.
 * Updates both rowHeight (pixels) and tableRowProperties.rowHeight (twips) attributes.
 *
 * @param {number} rowIndex - Index of the resized row
 * @param {number} newHeightPx - New row height in pixels
 */
function dispatchRowResizeTransaction(rowIndex, newHeightPx) {
  if (!props.tableElement) return;
  const targetEditor = resolveEditorForTableElement(props.editor, props.tableElement);
  if (targetEditor === null) {
    emit('resize-error', {
      rowIndex,
      error: 'Header/footer editor not available for table resize',
    });
    return;
  }
  const view = targetEditor?.view;
  if (!view) {
    emit('resize-error', {
      rowIndex,
      error: 'Editor view not available for table resize',
    });
    return;
  }

  try {
    const { state, dispatch } = view;
    const tr = state.tr;

    const tablePos = findTablePosition(state, props.tableElement);
    if (tablePos === null) {
      emit('resize-error', { rowIndex, error: 'Table position not found' });
      return;
    }

    const tableNode = state.doc.nodeAt(tablePos);
    if (!tableNode || tableNode.type.name !== 'table') {
      emit('resize-error', { rowIndex, error: 'Invalid table node' });
      return;
    }

    // Walk table children to find the tableRow at rowIndex
    let currentRowIdx = 0;
    let rowPos = null;
    let rowNode = null;

    tableNode.forEach((child, offset) => {
      if (child.type.name === 'tableRow' && currentRowIdx === rowIndex) {
        rowPos = tablePos + 1 + offset;
        rowNode = child;
      }
      if (child.type.name === 'tableRow') {
        currentRowIdx++;
      }
    });

    if (rowPos === null || !rowNode) {
      emit('resize-error', { rowIndex, error: 'Row not found at index' });
      return;
    }

    const heightTwips = pixelsToTwips(newHeightPx);
    const existingRowProps = rowNode.attrs.tableRowProperties || {};

    const newAttrs = {
      ...rowNode.attrs,
      rowHeight: newHeightPx,
      tableRowProperties: {
        ...existingRowProps,
        rowHeight: { value: heightTwips, rule: 'atLeast' },
      },
    };

    tr.setNodeMarkup(rowPos, null, newAttrs);
    dispatch(tr);

    invalidateTableMeasureCache(props.tableElement);

    emit('resize-success', { rowIndex, newHeight: newHeightPx });
  } catch (error) {
    emit('resize-error', {
      rowIndex,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Watch for changes to table element and reparse metadata
 */
watch(
  () => props.tableElement,
  () => {
    parseTableMetadata();
    updateOverlayRect();
    if (props.visible && props.tableElement) {
      startOverlayTracking();
    } else if (!props.tableElement) {
      stopOverlayTracking();
    }
  },
  { immediate: true },
);

/**
 * Watch for visibility changes
 */
watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      parseTableMetadata();
      updateOverlayRect();
      startOverlayTracking();
    } else {
      stopOverlayTracking();
      // Clean up drag state if overlay is hidden
      cancelActiveResizeDrag();
    }
  },
);

onMounted(() => {
  window.addEventListener('scroll', updateOverlayRect, true);
  window.addEventListener('resize', updateOverlayRect);
  updateOverlayRect();
});

/**
 * Clean up on unmount
 */
onBeforeUnmount(() => {
  // Set unmounted flag to prevent post-unmount throttled calls
  isUnmounted = true;

  // Cancel any pending throttled calls to prevent memory leaks
  mouseMoveThrottle.cancel();
  rowMouseMoveThrottle.cancel();
  stopOverlayTracking();

  if (dragState.value) {
    document.removeEventListener('mousemove', onDocumentMouseMove);
    document.removeEventListener('mouseup', onDocumentMouseUp);
  }

  if (rowDragState.value) {
    document.removeEventListener('mousemove', onRowDocumentMouseMove);
    document.removeEventListener('mouseup', onRowDocumentMouseUp);
  }

  // Re-enable PM pointer events
  if ((dragState.value || rowDragState.value) && props.editor?.view?.dom) {
    props.editor.view.dom.style.pointerEvents = 'auto';
  }

  removeInteractionCancelListeners();
  window.removeEventListener('scroll', updateOverlayRect, true);
  window.removeEventListener('resize', updateOverlayRect);
});
</script>

<style scoped>
.superdoc-table-resize-overlay {
  position: absolute;
  pointer-events: none;
  user-select: none;
}

.resize-handle {
  position: absolute;
  cursor: col-resize;
  user-select: none;
  z-index: 15;
}

.resize-handle::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 0;
  width: 2px;
  height: 100%;
  background-color: rgba(74, 144, 226, 0.3);
  transform: translateX(-1px);
  transition:
    background-color 0.2s ease,
    width 0.2s ease;
}

.resize-handle:hover::before {
  background-color: #4a90e2;
  width: 3px;
  transform: translateX(-1.5px);
}

.resize-handle--active::before {
  background-color: #4a90e2;
  width: 2px;
  transform: translateX(-1px);
}

.resize-handle--row {
  cursor: row-resize;
}

.resize-handle--row::before {
  left: 0;
  top: 50%;
  width: inherit;
  height: 2px;
  transform: translateY(-1px);
}

.resize-handle--row:hover::before {
  height: 3px;
  width: inherit;
  transform: translateY(-1.5px);
}

.resize-handle--row.resize-handle--active::before {
  height: 2px;
  width: inherit;
  transform: translateY(-1px);
}

.resize-guideline {
  position: absolute;
  background-color: #4a90e2;
  pointer-events: none;
  box-shadow: 0 0 4px rgba(74, 144, 226, 0.5);
}
</style>
